import { useEffect, useRef } from "react";
import { Carousel, type CarouselRefAttributes } from "~/common/components/carousel/Carousel";
import Widget from "~/features/cms/homepagePersonalization/components/Widget";
import { GlobalStyles, Stack, styled, useTheme } from "@schaeffler-commerce/design-system";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import type { CMSBannerDTO } from "~/features/cms/types/dto";

export interface RotatingWidgetsProps {
  timeout?: number;
  banners: CMSBannerDTO[];
  className?: string;
}

export const RotatingWidgets = ({ banners, timeout = 3000, className }: RotatingWidgetsProps) => {
  const carouselRef = useRef<CarouselRefAttributes>(null);
  const theme = useTheme();

  const pageAmount = banners.length;
  const isDesktop = useIsMinDesktopSize();

  useEffect(() => {
    if (pageAmount > 1) {
      const intervalId = setInterval(() => {
        if (carouselRef.current) {
          carouselRef.current.setNextPage();
        }
      }, timeout);
      return () => clearInterval(intervalId);
    }
  }, [timeout, pageAmount]);

  return (
    <>
      <GlobalStyles
        styles={{
          [`.${PAGINATION_ITEM}`]: {
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: theme.palette.divider,
            cursor: "pointer",
            "&:hover": {
              background: theme.palette.primary.dark,
            },
          },
          [`.${PAGINATION_ITEM_ACTIVE}`]: {
            width: 20,
            borderRadius: "10px",
            background: `${theme.palette.primary.main} !important`,
            cursor: "default",
            "&:hover": {
              background: `${theme.palette.primary.dark} !important`,
            },
          },
        }}
      />
      <Carousel
        className={className}
        data={banners}
        pageItemsCount={isDesktop ? 2 : 1}
        renderSlide={({ data }) => {
          return (
            <WidgetsContainer halfWidth={data.length == 1 && isDesktop}>
              {data.map((banner, index) => (
                <Widget key={index} data={banner} />
              ))}
            </WidgetsContainer>
          );
        }}
        ref={carouselRef}
        continuous={true}
        paginationItemActiveClassName={PAGINATION_ITEM_ACTIVE}
        paginationItemClassName={PAGINATION_ITEM}
        hideArrows
        // Migrated from CMSBanner.less (.scha__rotating-banners): rounded third-party carousel.
        sx={{ "& .react-swipe-container.carousel": { borderRadius: "12px" } }}
      />
    </>
  );
};

const PAGINATION_ITEM = "CmsRotatingWidget-paginationItem";
const PAGINATION_ITEM_ACTIVE = "CmsRotatingWidget-paginationItemActive";

const WidgetsContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "halfWidth",
})<{ halfWidth: boolean }>(({ halfWidth }) => ({
  flexDirection: "row",
  gap: "24px",
  width: halfWidth ? "50%" : "100%",
}));
